import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { Skeleton } from "@/components/ui/skeleton";
import { getUserFriendlyError } from "@/lib/error-handler";
import { useSeoMeta } from "@/hooks/useSeoMeta";
import AccountLayout from "./AccountLayout";

const PROFILE_TYPES = [
  { value: "banda", label: "Banda" },
  { value: "artista", label: "Artista" },
  { value: "promotora", label: "Promotora" },
  { value: "manager", label: "Manager" },
  { value: "tecnico", label: "Técnico/a" },
  { value: "otro", label: "Otro" },
];

export default function AccountProfile() {
  useSeoMeta({ pageType: "user_profile" });
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: profile, isLoading } = useQuery({
    queryKey: ["profile", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", user!.id)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!user,
  });

  const [form, setForm] = useState<Record<string, string>>({});

  // Merge profile into form defaults
  const getValue = (field: string) => {
    if (field in form) return form[field];
    if (profile) return (profile as any)[field] ?? "";
    return "";
  };

  const updateField = (field: string, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const mutation = useMutation({
    mutationFn: async () => {
      const updates: Record<string, any> = {
        visible_name: getValue("visible_name") || null,
        profile_type: getValue("profile_type") || null,
        city: getValue("city") || null,
        professional_link: getValue("professional_link") || null,
        bio: getValue("bio") || null,
        avatar_url: getValue("avatar_url") || null,
      };
      const { error } = await supabase
        .from("profiles")
        .update(updates as any)
        .eq("id", user!.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["profile", user?.id] });
      setForm({});
      toast({ title: "Perfil actualizado" });
    },
    onError: (err: any) => {
      toast({ title: "Error", description: getUserFriendlyError(err), variant: "destructive" });
    },
  });

  return (
    <AccountLayout>
      {isLoading ? (
        <Skeleton className="h-64" />
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Mi perfil</CardTitle>
            <p className="text-sm text-muted-foreground">
              Estos datos se usarán para identificarte en tus reviews públicas.
            </p>
          </CardHeader>
          <CardContent>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                mutation.mutate();
              }}
              className="space-y-4 max-w-lg"
            >
              <div className="space-y-2">
                <Label>Nombre visible *</Label>
                <Input
                  value={getValue("visible_name")}
                  onChange={(e) => updateField("visible_name", e.target.value)}
                  required
                  placeholder="Ej: Los Planetas"
                />
                <p className="text-xs text-muted-foreground">
                  Este nombre aparecerá públicamente en tus reviews
                </p>
              </div>

              <div className="space-y-2">
                <Label>Tipo de perfil</Label>
                <Select
                  value={getValue("profile_type")}
                  onValueChange={(v) => updateField("profile_type", v)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecciona" />
                  </SelectTrigger>
                  <SelectContent>
                    {PROFILE_TYPES.map((pt) => (
                      <SelectItem key={pt.value} value={pt.value}>
                        {pt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Ciudad</Label>
                <Input
                  value={getValue("city")}
                  onChange={(e) => updateField("city", e.target.value)}
                  placeholder="Ej: Madrid"
                />
              </div>

              <div className="space-y-2">
                <Label>URL del avatar</Label>
                <Input
                  value={getValue("avatar_url")}
                  onChange={(e) => updateField("avatar_url", e.target.value)}
                  placeholder="https://..."
                />
              </div>

              <div className="space-y-2">
                <Label>Enlace profesional</Label>
                <Input
                  value={getValue("professional_link")}
                  onChange={(e) => updateField("professional_link", e.target.value)}
                  placeholder="https://instagram.com/tu-perfil"
                />
              </div>

              <div className="space-y-2">
                <Label>Bio corta</Label>
                <Textarea
                  value={getValue("bio")}
                  onChange={(e) => updateField("bio", e.target.value)}
                  placeholder="Cuéntanos sobre ti..."
                  rows={3}
                />
              </div>

              <Button type="submit" disabled={mutation.isPending}>
                {mutation.isPending ? "Guardando..." : "Guardar cambios"}
              </Button>
            </form>
          </CardContent>
        </Card>
      )}
    </AccountLayout>
  );
}
